// libs
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { useSnackbar } from 'notistack'
import { setAccessToken } from 'lib/auth'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// constants
import { Modals } from 'constants/modals'
// types
import { IAuthUserWithToken } from 'types/graphql'
// graphql
import { AUTH_SIGN_IN_MUTATION } from 'graphql/auth/mutations'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
import { setTwoFactorToken } from 'store/reducers/twoFactorAuth'
// utils
import { toSubmissionError } from 'utils/errors/form'
import { rememberAuthMethod } from 'utils/authStorage'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'

export type LoginFormData = {
  email: string
  domain: string
  password: string
}

type AuthSignInResponse = { authSignIn: { data: IAuthUserWithToken } }

/** Email + password sign-in, including the 2FA-verify / 2FA-setup branches. */
export const usePasswordLogin = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { enqueueSnackbar } = useSnackbar()

  const [signInMutation] = useMutation<AuthSignInResponse>(AUTH_SIGN_IN_MUTATION, { context: { handledLocally: true } })

  const { showModal: showSetupModal } = useShowModal({ modalType: Modals.TWO_FACTOR_SETUP_MODAL })

  const login = async (formData: LoginFormData) => {
    try {
      const {
        data: {
          authSignIn: { data }
        }
      } = await signInMutation({
        variables: {
          email: formData.email.trim(),
          domain: `${formData.domain}.skefto.com`,
          password: formData.password.trim()
        }
      })

      rememberAuthMethod('password')

      // Check if 2FA is required
      if (data.twoFactorRequired && data.twoFactorToken) {
        // Store token in Redux (memory only, expires in 5 minutes)
        dispatch(
          setTwoFactorToken({
            token: data.twoFactorToken,
            expiryMinutes: 5
          })
        )

        // Redirect to 2FA verification page
        await router.push('/login/verify-2fa')
        return
      }

      // 2FA setup is required (company policy enforced)
      if (data.twoFactorSetupRequired && data.twoFactorSetupToken) {
        showSetupModal({
          setupToken: data.twoFactorSetupToken,
          onSuccess: () =>
            enqueueSnackbar('Two-factor authentication has been set up. Please log in again.', { variant: 'success' })
        })
        return
      }

      // Regular login flow (no 2FA)
      dispatch(setCurrentUser(data.user))
      setAccessToken(data.accessToken)

      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()

        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }
    } catch (errors: any) {
      throw toSubmissionError(errors)
    }
  }

  return { login }
}
